import React, { useEffect, useState } from 'react';
import { useTranslate } from 'core/translate';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalFooterButtons,
  ModalHeader,
  ModalFooterPrimaryButton,
} from '../../../../components/modal/Modal';
import { Translate } from 'core/translate';
import {
  keys,
  get,
  find,
  filter,
  findIndex,
  uniqBy,
  some,
  isEqual,
  compact,
} from 'lodash';
import { Link, useParams } from 'react-router-dom';
import CarouselNavigation from '../../../../components/carouselNavigation/CarouselNavigation';
import BodyDetailsModalImage from './BodyDetailsModalImage';
import RadioToggle from 'core/controls/radioToggle/RadioToggle';
import Clamp from 'react-multiline-clamp';
import { combineClassNames } from '../../../../../core/css';
import { Button } from '../../../../../core/controls/button/Button';
import { SvgIcon } from '../../../../../core/icons';

const BodyDetailsModalDropdown = ({
  powertrainOptions,
  selectedOption,
  onChange,
  analyticsKey,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const closeMenu = () => {
      if (menuOpen) {
        setMenuOpen(false);
      }
    };
    window.addEventListener('click', closeMenu);
    return () => {
      window.removeEventListener('click', closeMenu);
    };
  }, [menuOpen]);

  return (
    <div className="body-details-dropdown__container">
      <button
        className={`body-details-dropdown stat-dropdown${
          menuOpen ? ' body-details-dropdown--open' : ''
        }`}
        data-dtm={analyticsKey}
        onClick={() => setMenuOpen(true)}>
        <Clamp lines={2}>
          <div dangerouslySetInnerHTML={{ __html: selectedOption.powertrainLabel }} />
        </Clamp>
      </button>
      <div
        className={`body-details-dropdown__menu${
          menuOpen ? ' body-details-dropdown__menu--open' : ''
        }`}>
        <ul className="body-details-dropdown__list">
          {powertrainOptions.map(({ id, powertrainLabel }) => {
            return (
              <li
                key={id}
                className={`body-details-dropdown__option ${
                  id === selectedOption.id
                    ? 'body-details-dropdown__option--selected'
                    : ''
                }`}>
                <button
                  className="body-details-dropdown__btn-reset stat-dropdown"
                  data-dtm={analyticsKey}
                  onClick={() => onChange(id)}
                  dangerouslySetInnerHTML={{ __html: powertrainLabel }}
                />
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const NormalizeFeatureOptions = ({
  features,
  options,
  matrix,
  translate,
  filteredOptions,
  children,
}) => {
  const { bodyTypes } = matrix;
  const normalizedFeatureOptions = features.options.map((option) => {
    const id = +option.bodyTypeId;
    const bodyType = find(bodyTypes, { id });

    const fullOption = find(options, { styleId: option.styleId });
    const engineCode = get(fullOption, 'engine');
    const transmissionCode = get(fullOption, 'transmission');
    const engineData = find(matrix.engines, { id: engineCode });
    const transmissionData = find(matrix.transmissions, {
      id: transmissionCode,
    });

    const driveType = find(bodyType.driveTypes, {
      driveType: option.driveType,
    });

    const powertrainData = {
      id: `${engineData.id}_${transmissionData.id}`,
      transmissionCode: transmissionData.id,
      engineCode: engineData.id,
      powertrainLabel: translate('powertrain.combination.v3', {
        engine: engineData.description,
        transmission: transmissionData.description,
      }),
      show: !!engineData.description,
    };

    return {
      ...option,
      powertrainData,
      cgiImageUrl: fullOption,
      driveTypeDescription: get(driveType, 'description', ''),
      driveTypeLabel: get(driveType, 'label', ''),
      rearWheelType: get(bodyType, 'rearWheelType', ''),
      hasWheelBase: get(bodyType, 'wheelBaseType', ''),
    };
  });

  const normalizedBodyGroups = filteredOptions.map((bodyData) => {
    const bodyObj = find(bodyTypes, { id: +bodyData.bodyType });

    const bodyOptionIds = filter(bodyTypes, {
      description: bodyObj.description,
    }).map(({ id }) => id.toString());

    return {
      ...bodyData,
      availableOptions: filter(normalizedFeatureOptions, (opt) =>
        bodyOptionIds.includes(opt.bodyTypeId)
      ),
    };
  });

  return children(normalizedBodyGroups);
};

const filterOptionsByDT = (options, { driveType, rearWheelType }) => {
  return filter(options, (option) => {
    return (
      option.driveType === driveType && option.rearWheelType === rearWheelType
    );
  });
};

const BodyDetailsModalFilter = ({
  selectedGroup,
  selections,
  onStyleChange,
  translate,
}) => {
  const [selectedDTFilter, setSelectedDTFilter] = useState(null);
  const [selectedPTFilter, setSelectedPTFilter] = useState(null);
  const [lastSelected, setLastSelected] = useState(null);

  const analyticsKey = `${selectedGroup?.description}:filters`;

  const driveAndWheelTypes = selectedGroup.availableOptions.map((x) => {
    const {
      driveType,
      rearWheelType,
      driveTypeDescription,
      driveTypeLabel,
    } = x;
    return {
      id: `${driveType}-${rearWheelType}`,
      driveType,
      rearWheelType,
      description: driveTypeDescription,
      label: driveTypeLabel,
    };
  });

  const getFilteredPowertrains = () => {
    return compact(
      uniqBy(
        filterOptionsByDT(selectedGroup.availableOptions, selectedDTFilter).map(
          (option) => option.powertrainData.show && option.powertrainData
        ),
        'id'
      )
    );
  };

  const setSelectedPT = (drive) => {
    const { powertrainData } = filterOptionsByDT(
      selectedGroup.availableOptions,
      drive
    )[0];

    setSelectedPTFilter(powertrainData);
  };

  const driveOptions = uniqBy(driveAndWheelTypes, 'id');
  const powertrainOptions =
    (selectedDTFilter && getFilteredPowertrains()) || [];

  useEffect(() => {
    if (!isEqual(lastSelected, selectedGroup)) {
      let selectedDT = find(driveOptions, {
        rearWheelType: selections.rearWheelType,
        driveType: selections.driveType,
      });
      if (selectedDT) {
        setSelectedDTFilter(selectedDT);
      } else {
        selectedDT = find(driveOptions, {
          driveType: selections.driveType,
        });
        setSelectedDTFilter(selectedDT ? selectedDT : driveOptions[0]);
      }
      setSelectedPT(selectedDT ? selectedDT : driveOptions[0]);
      setLastSelected(selectedGroup);
    }
  }, [selectedGroup]);

  useEffect(() => {
    onStyleChange(
      (
        (selectedGroup &&
          selectedPTFilter &&
          setSelectedDTFilter &&
          find(
            selectedGroup.availableOptions,
            ({ powertrainData, driveType, rearWheelType }) => {
              const { transmissionCode, engineCode } = powertrainData;
              const {
                driveType: filterDriveType,
                rearWheelType: filterRearWheelType,
              } = selectedDTFilter;
              const {
                engineCode: filterEngineCode,
                transmissionCode: filterTransmissionCode,
              } = selectedPTFilter;

              return (
                transmissionCode === filterTransmissionCode &&
                engineCode === filterEngineCode &&
                driveType === filterDriveType &&
                rearWheelType === filterRearWheelType
              );
            }
          )) ||
        {}
      ).styleId
    );
  }, [selectedPTFilter]);

  const onDriveChange = ({ id }) => {
    const drive = find(driveOptions, { id });
    setSelectedDTFilter(drive);
    setSelectedPT(drive);
  };

  const onPTSelect = (id) => {
    setSelectedPTFilter(find(powertrainOptions, { id }));
  };

  return (
    <>
      {selectedPTFilter && (
        <div className="body-details-filters">
          {driveOptions.length > 1 && (
            <div className="body-details-filters__input-group">
              <label className="body-details-filters__label">
                {translate('component.common.select.drive.type')}:
                <RadioToggle
                  options={driveOptions}
                  onChange={onDriveChange}
                  isSelectionRevealed={true}
                  analyticsKey={analyticsKey}
                  className="body-details-filters__drive-radio"
                  selected={(x) =>
                    x.driveType === selectedDTFilter.driveType &&
                    x.rearWheelType === selectedDTFilter.rearWheelType
                  }
                  renderLabel={(x) => (
                    <>
                      <div>
                        <div className="drive-type-toggle__title">
                          {x.label}
                        </div>
                        {x.rearWheelType && (
                          <div className="drive-type-toggle__subtitle">
                            {x.rearWheelType}
                          </div>
                        )}
                        {!x.rearWheelType && x.description && (
                          <div className="drive-type-toggle__subtitle">
                            {x.description}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                />
              </label>
            </div>
          )}
          {powertrainOptions?.length > 0 && (
            <div className="body-details-filters__input-group">
              <label className="body-details-filters__label">
                {translate('component.standards.group.powertrain')}:
                {powertrainOptions?.length > 1 && (
                  <BodyDetailsModalDropdown
                    powertrainOptions={powertrainOptions}
                    selectedOption={selectedPTFilter}
                    onChange={onPTSelect}
                    analyticsKey={analyticsKey}
                  />
                )}
                {powertrainOptions?.length === 1 && (
                  <div
                    className="body-details-filters__powertrain-value"
                    dangerouslySetInnerHTML={{
                      __html: powertrainOptions[0].powertrainLabel,
                    }}
                  />
                )}
              </label>
            </div>
          )}
        </div>
      )}
    </>
  );
};

const BodyDetailsTable = ({ dimensions }) => {
  const dimensionRows = dimensions.map((dimension, i) => {
    return (
      <tr
        className={
          'body-details-table__row' && i % 2 === 0
            ? ' body-details-table__row--even'
            : ''
        }
        key={dimension.id}>
        <td className="body-details-table__cell body-details-table__cell--description">
          {dimension.description}
        </td>
        <td className="body-details-table__cell">{dimension.value}</td>
      </tr>
    );
  });

  return (
    <table className="body-details-table">
      <thead>
        <tr>
          <th className="body-details-table__header" colSpan="2">
            <Translate value="component.dimensions.title" />
          </th>
        </tr>
      </thead>
      <tbody className="body-details-table__body">{dimensionRows}</tbody>
    </table>
  );
};

const BodyDetailsModalHeader = ({
  selectedGroup,
  features,
  translate,
  selections,
  setStyleId,
  bodyTypeDescription,
}) => {
  return (
    <div className="body-details-modal__header">
      {selectedGroup && (
        <BodyDetailsModalImage
          cgiImageUrl={selectedGroup.cgiImageUrl}
          alt={selectedGroup.description}
        />
      )}
      <BodyDetailsModalFilter
        selectedGroup={selectedGroup}
        features={features}
        selections={selections}
        onStyleChange={setStyleId}
        translate={translate}
        bodyTypeDescription={bodyTypeDescription}
      />
    </div>
  );
};

const BodyDetailsModalBody = ({ styleId, features }) => {
  try {
    const groupsDimensions = features.groups.Dimensions;
    const styleDimensions = features.styleIds[styleId].Dimensions;

    const dimensions = keys(groupsDimensions).map(function (key) {
      return {
        id: key,
        description: groupsDimensions[key].description,
        value: styleDimensions[key],
      };
    });

    return (
      <>
        <BodyDetailsTable dimensions={dimensions} />
      </>
    );
  } catch (e) {
    return <Translate value="component.dimensions.error" />;
  }
};

const BodyDetailsModalInner = ({
  features,
  onClose,
  filteredOptions,
  selections,
  normalizedBodyGroups,
  onChange,
  isBodyTypeRevealed,
  matrix,
}) => {
  const { id } = useParams();
  const translate = useTranslate();
  const { bodyTypes } = matrix;

  const header = translate('body.detail.header.v3');

  const [activeBodyIndex, setActiveBodyIndex] = useState(null);
  const [styleId, setStyleId] = useState(null);

  const compareLink = `../../compare/${id}`;

  useEffect(() => {
    setActiveBodyIndex(findIndex(normalizedBodyGroups, { bodyType: +id }));
  }, []);

  const onSlideChange = ({ bodyType }) => {
    setActiveBodyIndex(findIndex(normalizedBodyGroups, { bodyType }));
  };

  const carousel = (
    <CarouselNavigation
      slideData={filteredOptions.map((option) => {
        return {
          ...option,
          slideKey: option.bodyType,
          primaryText: option.description,
        };
      })}
      navIndex={activeBodyIndex}
      smallLabelNext={translate('byo.page.general.next.step.v3')}
      labelNext={translate('byo.page.general.next.step.v3')}
      smallLabelPrev={translate('byo.page.general.prev.step.mobile.v3')}
      labelPrev={translate('byo.page.general.prev.step')}
      onSlideChange={(data) => onSlideChange(data)}
      analyticsKey="body nav"
      modifier="body-details"
    />
  );

  if (!activeBodyIndex && activeBodyIndex !== 0) {
    return null;
  }

  const selectedGroup = normalizedBodyGroups[activeBodyIndex];

  const getIsSelected = () => {
    if (isBodyTypeRevealed) {
      if (+selectedGroup.styleId === selections.styleId) {
        return true;
      }
      if (
        some(selectedGroup.availableOptions, {
          styleId: `${selections.styleId}`,
        })
      ) {
        return true;
      }
    }
    return false;
  };

  const isSelected = getIsSelected();
  const bodyTypeDescription = (
    find(bodyTypes, { id: selections.bodyType }) || {}
  ).description;
  // Based on the requirements, body compare should be enabled at the model level so we're only checking the first driveType on the first bodyType
  // since they should all be the same
  const displayCompare =
    bodyTypes?.length > 1 && bodyTypes[0].driveTypes[0].compareAll;

  const onSelect = () => {
    if (!isSelected) {
      onChange(selectedGroup);
    }
    onClose();
  };

  const footerButtonAnalytics = selectedGroup?.description + ':bottom bar';

  return (
    <Modal isOpen={true} contentLabel={header} onRequestClose={onClose}>
      <ModalHeader
        text={header}
        analyticsKey={selectedGroup?.description}
        onClose={onClose}
        contentUnderHeader={carousel}
        bodyTypeDescription={bodyTypeDescription}
      />
      <ModalBody innerClassName="body-details-modal__inner">
        <BodyDetailsModalHeader
          selectedGroup={selectedGroup}
          features={features}
          translate={translate}
          selections={selections}
          setStyleId={setStyleId}
          bodyTypeDescription={bodyTypeDescription}
        />
        <BodyDetailsModalBody
          styleId={styleId}
          features={features}
          bodyTypeDescription={bodyTypeDescription}
        />
        {displayCompare && (
          <Button
            as={Link}
            to={compareLink}
            className="compare-options__button compare-options__button--body stat-button-link">
            <SvgIcon name="compare" className="button__icon--left" />
            <span className="compare-options__label">
              <Translate value="trim.compare.all.body.v3" />
            </span>
          </Button>
        )}
      </ModalBody>
      <ModalFooter>
        <ModalFooterButtons classModifier="body-details">
          <ModalFooterPrimaryButton
            className={combineClassNames(
              'body-details__select-button',
              'stat-button-link'
            )}
            onSelect={onSelect}
            data-dtm={footerButtonAnalytics}>
            {isSelected ? null : translate('select')}
          </ModalFooterPrimaryButton>
        </ModalFooterButtons>
      </ModalFooter>
    </Modal>
  );
};

const BodyDetailsModal = (props) => {
  const translate = useTranslate();
  return (
    <NormalizeFeatureOptions {...props} translate={translate}>
      {(normalizedBodyGroups) => (
        <BodyDetailsModalInner
          {...props}
          normalizedBodyGroups={normalizedBodyGroups}
        />
      )}
    </NormalizeFeatureOptions>
  );
};

export default BodyDetailsModal;
